/** "Report a problem" (0.9.12): reachable from every screen. The rider writes a line; if they agree, the app
 * adds what they were doing (screen, app version, browser, screen width, the last error id). Admins read the
 * reports in Settings → Riders → Reports. */
import { useState, type FormEvent } from "react";
import { useLocation } from "react-router";
import { lastErrorId } from "@/api/client";
import { useSendFeedback } from "@/api/hooks";
import { useToast } from "@/components/Toast";
import { Dialog } from "@/components/ui";

export const REPORT_MAX = 2000;

/** What goes along with a report when the rider agrees: never more than these five things. */
export function reportContext(route: string): Record<string, string> {
  const ctx: Record<string, string> = {
    route: route.slice(0, 200),
    version: __APP_VERSION__,
    browser: navigator.userAgent.slice(0, 200),
    screen: `${window.innerWidth}x${window.innerHeight}`,
  };
  const eid = lastErrorId();
  if (eid) ctx.error_id = eid;
  return ctx;
}

export function ReportProblem({
  className = "",
  label = "Report a problem",
}: {
  className?: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={`report-link ${className}`.trim()} onClick={() => setOpen(true)}>
        {label}
      </button>
      {open && <ReportDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function ReportDialog({ onClose }: { onClose: () => void }) {
  const { pathname } = useLocation();
  const send = useSendFeedback();
  const toast = useToast();
  const [text, setText] = useState("");
  const [withContext, setWithContext] = useState(true);
  const left = REPORT_MAX - text.length;
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    send
      .mutateAsync({ text: text.trim(), context: withContext ? reportContext(pathname) : null })
      .then(() => {
        toast.ok("Thank you: your report was sent");
        onClose();
      }, toast.error);
  }
  return (
    <Dialog open onClose={onClose} title="Report a problem">
      <form className="stack" onSubmit={submit} data-testid="report-form">
        <label>
          What happened, or what is missing?
          <textarea
            value={text}
            maxLength={REPORT_MAX}
            rows={5}
            required
            onChange={(e) => setText(e.target.value)}
            placeholder="For example: the plan shows no ride on Saturday, though I said I can ride."
          />
        </label>
        <small className="faint" aria-live="polite">
          {left < 200 ? `${left} characters left` : ""}
        </small>
        <label className="check">
          <input type="checkbox" checked={withContext} onChange={(e) => setWithContext(e.target.checked)} />
          <span>
            Include what I was doing
            <br />
            <small className="faint">
              The screen you are on, the app version, your browser, the screen size and the last error number.
            </small>
          </span>
        </label>
        <div className="row">
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary" type="submit" disabled={send.isPending || !text.trim()}>
            {send.isPending ? "Sending…" : "Send report"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
